import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faArrowRight,
  faCheck,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import useOnboardingProgress from "../../helpers/hooks/useOnboardingProgress";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { buildSupportWhatsAppUrl } from "../../helpers/supportWhatsapp";

const whatsappUrl = buildSupportWhatsAppUrl(
  "Hola equipo Homelan, quiero compartirles la información de mi condominio para que me ayuden a preparar el archivo Excel de la carga inicial. Luego lo subiré a Homelan."
);

const InitialLoadHelpModal = ({ onClose }) => {
  const { enabled, isLoading, error, steps } = useOnboardingProgress();
  const [dismissed, setDismissed] = useState(false);
  // Se cierra una sola vez por carga del Inicio, asi que `onClose` (el
  // resaltado de la carga inicial) tambien se dispara una sola vez.
  const close = () => {
    if (dismissed) return;
    setDismissed(true);
    if (onClose) onClose();
  };

  useEffect(() => {
    if (enabled && error) {
      handleErrorMessage(error);
    }
  }, [enabled, error]);

  const isOpen =
    enabled &&
    !isLoading &&
    !error &&
    !dismissed &&
    steps.slice(0, 5).some((step) => !step.done);

  return (
    <Modal
      isOpen={!!isOpen}
      toggle={close}
      centered
      scrollable
      className="initial-load-help"
      contentClassName="initial-load-help__content"
      labelledBy="initial-load-help-title"
      aria-describedby="initial-load-help-description"
    >
      <ModalHeader
        toggle={close}
        tag="div"
        className="initial-load-help__hero"
        closeAriaLabel="Cerrar ayuda de carga inicial"
      >
        <span className="initial-load-help__eyebrow">
          TE ACOMPAÑAMOS DESDE EL PRIMER DÍA
        </span>
        <div className="initial-load-help__illustration" aria-hidden="true">
          <FontAwesomeIcon icon={faFileExcel} />
          <span className="initial-load-help__check">
            <FontAwesomeIcon icon={faCheck} />
          </span>
        </div>
        <h2 id="initial-load-help-title" className="initial-load-help__title">
          Te ayudamos a preparar
          <br />
          tu Excel de carga inicial.
        </h2>
        <p className="initial-load-help__subtitle">
          Comparte la información. Recibe tu Excel listo para subir.
        </p>
      </ModalHeader>
      <ModalBody className="initial-load-help__body">
        <p id="initial-load-help-description" className="text-700 mb-3">
          Comparte por WhatsApp la información de tu condominio.
          <strong className="text-900">
            {" "}
            Nuestro equipo prepara el archivo Excel para que tú lo subas a
            Homelan.
          </strong>
        </p>
        <ul
          className="initial-load-help__tags text-700"
          aria-label="Información que puedes incluir"
        >
          {["Propietarios", "Alícuotas", "Saldos iniciales"].map((label) => (
            <li key={label} className="bg-light">
              <FontAwesomeIcon icon={faCheck} className="text-success mr-2" />
              {label}
            </li>
          ))}
        </ul>
        <ol className="initial-load-help__steps bg-light">
          <li>
            <span className="initial-load-help__number bg-soft-primary text-primary">
              1
            </span>
            <div>
              <strong className="text-900">Comparte la información</strong>
              <span className="text-700">
                Escríbenos con los datos de tu condominio.
              </span>
            </div>
          </li>
          <li>
            <span className="initial-load-help__number bg-soft-primary text-primary">
              2
            </span>
            <div>
              <strong className="text-900">Recibe tu Excel y súbelo</strong>
              <span className="text-700">
                Preparamos el archivo y te lo enviamos para que lo subas a
                Homelan.
              </span>
            </div>
          </li>
        </ol>
      </ModalBody>
      <ModalFooter className="initial-load-help__footer">
        <Button
          color="success"
          className="initial-load-help__whatsapp"
          tag="a"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={close}
        >
          <FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
          Solicitar ayuda por WhatsApp
          <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
        </Button>
        <span className="initial-load-help__hint text-700">
          Se abrirá WhatsApp para que compartas la información.
        </span>
        <Button
          color="link"
          className="initial-load-help__skip text-700"
          onClick={close}
        >
          Prefiero continuar por mi cuenta
        </Button>
      </ModalFooter>
    </Modal>
  );
};

InitialLoadHelpModal.propTypes = {
  onClose: PropTypes.func,
};

export default InitialLoadHelpModal;
